import React, { useState, useEffect } from 'react';
import { FovModal, FovSelect, FovOption, FovToggle, VerticalSpacer } from 'components';
import {
  ModelSelectWrapper,
  SimpleFieldLabel,
  SimpleFieldInput,
  SimpleToggleRow,
  SimpleToggleList,
  GenerateButton,
} from '../imageCanvasStyles';
import {
  TRIPO_MODEL_VERSIONS,
  TRIPO_TEXTURE_QUALITIES,
  TRIPO_TEXTURE_ALIGNMENTS,
  TRIPO_GEOMETRY_QUALITIES,
  TRIPO_ORIENTATIONS,
  tripoFaceLimitRange,
  validateTripoSettings,
} from '../tripoSettings';

const ALIGNMENT_LABELS = { original_image: 'Original Image', geometry: 'Geometry' };
const ORIENTATION_LABELS = { default: 'Default', align_image: 'Align Image' };

const clampFaceLimit = (value, smartLowPoly) => {
  if (value === '' || value === null || value === undefined) return '';
  const { min, max } = tripoFaceLimitRange(smartLowPoly);
  return Math.min(max, Math.max(min, Number(value) || min));
};

const TripoSettingsModal = ({ theme, selectedTheme, visible, settings, onCancel, onSave }) => {
  const shared = { theme, $selectedTheme: selectedTheme };
  const [ draft, setDraft ] = useState(settings);

  useEffect(() => {
    if (visible) setDraft(settings);
  }, [ visible, settings ]);

  const handleSmartLowPolyChange = value => {
    setDraft({ ...draft, smartLowPoly: value, faceLimit: clampFaceLimit(draft.faceLimit, value) });
  };

  const handleFaceLimitChange = e => {
    setDraft({ ...draft, faceLimit: e.target.value });
  };

  const handleFaceLimitBlur = () => {
    setDraft({ ...draft, faceLimit: clampFaceLimit(draft.faceLimit, draft.smartLowPoly) });
  };

  const handleSave = () => {
    const { valid } = validateTripoSettings(draft);
    if (!valid) return;
    onSave(draft);
  };

  const texturing = draft.pbr || draft.texture;
  const { min: faceLimitMin, max: faceLimitMax } = tripoFaceLimitRange(draft.smartLowPoly);

  return (
    <FovModal title='Tripo AI Settings' visible={visible} onClose={onCancel} width={360}>
      <SimpleFieldLabel {...shared}>Model Version</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.modelVersion}
          placeholder={draft.modelVersion}
          onChange={(e, newVal) => setDraft({ ...draft, modelVersion: newVal })}
        >
          {TRIPO_MODEL_VERSIONS.map(value => (
            <FovOption key={value} value={value}>{value}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      <SimpleFieldLabel {...shared}>Geometry Quality</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.geometryQuality}
          placeholder={draft.geometryQuality}
          onChange={(e, newVal) => setDraft({ ...draft, geometryQuality: newVal })}
        >
          {TRIPO_GEOMETRY_QUALITIES.map(value => (
            <FovOption key={value} value={value}>{value}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      <SimpleFieldLabel {...shared}>Orientation</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.orientation}
          placeholder={ORIENTATION_LABELS[draft.orientation]}
          onChange={(e, newVal) => setDraft({ ...draft, orientation: newVal })}
        >
          {TRIPO_ORIENTATIONS.map(value => (
            <FovOption key={value} value={value}>{ORIENTATION_LABELS[value]}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      <SimpleFieldLabel {...shared}>Face Limit (blank = adaptive)</SimpleFieldLabel>
      <SimpleFieldInput
        {...shared}
        type='number'
        min={faceLimitMin}
        max={faceLimitMax}
        placeholder='Adaptive'
        value={draft.faceLimit}
        onChange={handleFaceLimitChange}
        onBlur={handleFaceLimitBlur}
      />

      {texturing && (
        <>
          <SimpleFieldLabel {...shared}>Texture Quality</SimpleFieldLabel>
          <ModelSelectWrapper>
            <FovSelect
              value={draft.textureQuality}
              placeholder={draft.textureQuality}
              onChange={(e, newVal) => setDraft({ ...draft, textureQuality: newVal })}
            >
              {TRIPO_TEXTURE_QUALITIES.map(value => (
                <FovOption key={value} value={value}>{value}</FovOption>
              ))}
            </FovSelect>
          </ModelSelectWrapper>

          <SimpleFieldLabel {...shared}>Texture Alignment</SimpleFieldLabel>
          <ModelSelectWrapper>
            <FovSelect
              value={draft.textureAlignment}
              placeholder={ALIGNMENT_LABELS[draft.textureAlignment]}
              onChange={(e, newVal) => setDraft({ ...draft, textureAlignment: newVal })}
            >
              {TRIPO_TEXTURE_ALIGNMENTS.map(value => (
                <FovOption key={value} value={value}>{ALIGNMENT_LABELS[value]}</FovOption>
              ))}
            </FovSelect>
          </ModelSelectWrapper>
        </>
      )}

      <SimpleFieldLabel {...shared}>Style (blank = none)</SimpleFieldLabel>
      <SimpleFieldInput
        {...shared}
        type='text'
        placeholder='e.g. object:clay'
        value={draft.style}
        onChange={e => setDraft({ ...draft, style: e.target.value })}
      />

      <SimpleFieldLabel {...shared}>Model Seed (blank = random)</SimpleFieldLabel>
      <SimpleFieldInput
        {...shared}
        type='number'
        placeholder='Random'
        value={draft.modelSeed}
        onChange={e => setDraft({ ...draft, modelSeed: e.target.value })}
      />

      <SimpleFieldLabel {...shared}>Texture Seed (blank = random)</SimpleFieldLabel>
      <SimpleFieldInput
        {...shared}
        type='number'
        placeholder='Random'
        value={draft.textureSeed}
        onChange={e => setDraft({ ...draft, textureSeed: e.target.value })}
      />

      <VerticalSpacer size={15} />

      <SimpleToggleList>
        <SimpleToggleRow {...shared}>
          Texture

          <FovToggle
            name='textureToggle'
            checked={!!(draft.pbr || draft.texture)}
            disabled={draft.pbr}
            callback={value => setDraft({ ...draft, texture: value })}
          />
        </SimpleToggleRow>

        <SimpleToggleRow {...shared}>
          PBR

          <FovToggle
            name='pbrToggle'
            checked={!!draft.pbr}
            callback={value => setDraft({ ...draft, pbr: value })}
          />
        </SimpleToggleRow>

        <SimpleToggleRow {...shared}>
          Auto Size

          <FovToggle
            name='autoSizeToggle'
            checked={!!draft.autoSize}
            callback={value => setDraft({ ...draft, autoSize: value })}
          />
        </SimpleToggleRow>

        <SimpleToggleRow {...shared}>
          Smart Low Poly

          <FovToggle
            name='smartLowPolyToggle'
            checked={!!draft.smartLowPoly}
            callback={handleSmartLowPolyChange}
          />
        </SimpleToggleRow>

        <SimpleToggleRow {...shared}>
          Compress

          <FovToggle
            name='compressToggle'
            checked={!!draft.compress}
            callback={value => setDraft({ ...draft, compress: value })}
          />
        </SimpleToggleRow>

        <SimpleToggleRow {...shared}>
          Generate Parts

          <FovToggle
            name='generatePartsToggle'
            checked={!!draft.generateParts}
            callback={value => setDraft({ ...draft, generateParts: value })}
          />
        </SimpleToggleRow>
      </SimpleToggleList>

      <VerticalSpacer size={20} />

      <GenerateButton {...shared} onClick={handleSave}>
        Save
      </GenerateButton>
    </FovModal>
  );
};

export { TripoSettingsModal };
